import { styled } from 'styled-system/jsx'

const PageStack = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
})

const PageHeader = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
})

const PageTitle = styled('h1', {
  base: {
    fontFamily: 'display',
    fontSize: '26px',
    fontWeight: '700',
    color: 'quickpass.ink',
  },
})

const PageSubtitle = styled('p', {
  base: {
    fontFamily: 'body',
    fontSize: '14px',
    color: 'quickpass.inkSoft',
  },
})

const QuietText = styled('p', {
  base: {
    fontFamily: 'body',
    fontSize: '14px',
    color: 'quickpass.inkSoft',
  },
})

const TabRow = styled('div', {
  base: {
    display: 'flex',
    gap: '8px',
  },
})

const TabButton = styled('button', {
  base: {
    flex: '1',
    paddingY: '10px',
    borderRadius: 'pill',
    fontFamily: 'body',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    border: '1px solid #E4E0EF',
    transition: 'all {durations.fast} {easings.appleStandard}',
  },
  variants: {
    active: {
      true: {
        backgroundColor: 'quickpass.primary',
        color: '#FFFFFF',
        borderColor: 'quickpass.primary',
      },
      false: {
        backgroundColor: '#FFFFFF',
        color: 'quickpass.ink',
        _hover: { borderColor: 'quickpass.primary' },
      },
    },
  },
  defaultVariants: {
    active: false,
  },
})

const HeaderRow = styled('div', {
  base: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
  },
})

const SubHeading = styled('h2', {
  base: {
    fontFamily: 'display',
    fontSize: '18px',
    fontWeight: '700',
    color: 'quickpass.ink',
  },
})

const PrimaryButton = styled('button', {
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    paddingX: '16px',
    paddingY: '9px',
    borderRadius: 'pill',
    fontFamily: 'body',
    fontSize: '13px',
    fontWeight: '600',
    color: '#FFFFFF',
    backgroundColor: 'quickpass.primary',
    border: 'none',
    cursor: 'pointer',
    transition: 'opacity {durations.fast} {easings.appleStandard}',
    _hover: { opacity: '0.88' },
    _disabled: { opacity: '0.5', cursor: 'default' },
  },
})

const GhostButton = styled('button', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    fontWeight: '600',
    color: 'quickpass.inkSoft',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    _hover: { opacity: '0.7' },
  },
})

const Composer = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
})

const FieldLabel = styled('label', {
  base: {
    fontFamily: 'body',
    fontSize: '12px',
    fontWeight: '600',
    color: 'quickpass.inkSoft',
    marginBottom: '4px',
  },
})

const TextInput = styled('input', {
  base: {
    width: '100%',
    paddingX: '12px',
    paddingY: '10px',
    borderRadius: 'md',
    border: '1px solid #E4E0EF',
    fontFamily: 'body',
    fontSize: '14px',
    color: 'quickpass.ink',
    backgroundColor: '#FFFFFF',
    _focus: { outline: 'none', borderColor: 'quickpass.primary' },
  },
})

const TextArea = styled('textarea', {
  base: {
    width: '100%',
    minHeight: '88px',
    paddingX: '12px',
    paddingY: '10px',
    borderRadius: 'md',
    border: '1px solid #E4E0EF',
    fontFamily: 'body',
    fontSize: '14px',
    color: 'quickpass.ink',
    backgroundColor: '#FFFFFF',
    resize: 'vertical',
    _focus: { outline: 'none', borderColor: 'quickpass.primary' },
  },
})

const SelectInput = styled('select', {
  base: {
    width: '100%',
    paddingX: '12px',
    paddingY: '10px',
    borderRadius: 'md',
    border: '1px solid #E4E0EF',
    fontFamily: 'body',
    fontSize: '14px',
    color: 'quickpass.ink',
    backgroundColor: '#FFFFFF',
    cursor: 'pointer',
    _focus: { outline: 'none', borderColor: 'quickpass.primary' },
  },
})

const ComposerActions = styled('div', {
  base: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '12px',
  },
})

const PostList = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
})

const PostTitleRow = styled('div', {
  base: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '10px',
  },
})

const PostTitle = styled('button', {
  base: {
    textAlign: 'left',
    fontFamily: 'display',
    fontSize: '16px',
    fontWeight: '700',
    color: 'quickpass.ink',
    backgroundColor: 'transparent',
    border: 'none',
    padding: '0',
    cursor: 'pointer',
    _hover: { color: 'quickpass.primary' },
  },
})

const ChipRow = styled('div', {
  base: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    marginTop: '6px',
  },
})

const CategoryChip = styled('span', {
  base: {
    paddingX: '10px',
    paddingY: '3px',
    borderRadius: 'pill',
    backgroundColor: 'quickpass.primarySoft',
    color: 'quickpass.primary',
    fontFamily: 'body',
    fontSize: '11px',
    fontWeight: '600',
  },
})

const ResolvedBadge = styled('span', {
  base: {
    paddingX: '10px',
    paddingY: '3px',
    borderRadius: 'pill',
    backgroundColor: '#E7F8EF',
    color: '#1E7A4D',
    fontFamily: 'body',
    fontSize: '11px',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    whiteSpace: 'nowrap',
  },
})

const AdminBadge = styled('span', {
  base: {
    paddingX: '8px',
    paddingY: '2px',
    borderRadius: 'pill',
    backgroundColor: 'quickpass.primary',
    color: '#FFFFFF',
    fontFamily: 'body',
    fontSize: '10px',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    whiteSpace: 'nowrap',
  },
})

const PostMeta = styled('p', {
  base: {
    fontFamily: 'body',
    fontSize: '12px',
    color: 'quickpass.inkSoft',
    marginTop: '8px',
  },
})

const PostBody = styled('p', {
  base: {
    fontFamily: 'body',
    fontSize: '14px',
    color: 'quickpass.ink',
    whiteSpace: 'pre-wrap',
    marginTop: '10px',
  },
})

const ActionRow = styled('div', {
  base: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    marginTop: '12px',
  },
})

const ActionButton = styled('button', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    fontWeight: '600',
    color: 'quickpass.primary',
    backgroundColor: 'transparent',
    border: 'none',
    padding: '0',
    cursor: 'pointer',
    transition: 'opacity {durations.fast} {easings.appleStandard}',
    _hover: { opacity: '0.7' },
    _disabled: { opacity: '0.5', cursor: 'default' },
  },
  variants: {
    tone: {
      neutral: { color: 'quickpass.primary' },
      danger: { color: '#9B3B3B' },
    },
  },
  defaultVariants: {
    tone: 'neutral',
  },
})

const Divider = styled('div', {
  base: {
    height: '1px',
    backgroundColor: '#EFEDF5',
    marginY: '12px',
  },
})

const ReplyList = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
})

const ReplyItem = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    paddingX: '12px',
    paddingY: '8px',
    borderRadius: 'md',
    backgroundColor: '#F7F6FB',
  },
})

const ReplyHandleRow = styled('div', {
  base: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
  },
})

const ReplyHandle = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '12px',
    fontWeight: '700',
    color: 'quickpass.ink',
  },
})

const ReplyBody = styled('p', {
  base: {
    fontFamily: 'body',
    fontSize: '14px',
    color: 'quickpass.ink',
    whiteSpace: 'pre-wrap',
  },
})

const ReplyForm = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginTop: '12px',
  },
})

const ReportPanel = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginTop: '12px',
    paddingTop: '12px',
    borderTop: '1px solid #EFEDF5',
  },
})

const InlineNote = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '12px',
    color: 'quickpass.inkSoft',
  },
})

const ErrorBanner = styled('div', {
  base: {
    paddingX: '16px',
    paddingY: '12px',
    borderRadius: 'md',
    backgroundColor: '#FBECEC',
    color: '#9B3B3B',
    fontFamily: 'body',
    fontSize: '14px',
    fontWeight: '600',
  },
})

const NoticeBanner = styled('div', {
  base: {
    paddingX: '16px',
    paddingY: '12px',
    borderRadius: 'md',
    backgroundColor: 'quickpass.primarySoft',
    color: 'quickpass.primary',
    fontFamily: 'body',
    fontSize: '14px',
    fontWeight: '600',
  },
})

const VoteControl = styled('div', {
  base: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginTop: '12px',
  },
})

const VoteButton = styled('button', {
  base: {
    width: '34px',
    height: '34px',
    borderRadius: 'md',
    border: '1px solid #E4E0EF',
    backgroundColor: '#FFFFFF',
    fontFamily: 'body',
    fontSize: '15px',
    fontWeight: '700',
    color: 'quickpass.inkSoft',
    cursor: 'pointer',
    transition: 'all {durations.fast} {easings.appleStandard}',
    _hover: { borderColor: 'quickpass.primary', color: 'quickpass.primary' },
    _disabled: { opacity: '0.5', cursor: 'default' },
  },
})

const VoteScore = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '15px',
    fontWeight: '700',
    color: 'quickpass.ink',
    minWidth: '28px',
    textAlign: 'center',
  },
})

const ImageRow = styled('div', {
  base: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    alignItems: 'center',
  },
})

const ImageThumbWrapper = styled('div', {
  base: {
    position: 'relative',
    width: '72px',
    height: '72px',
    borderRadius: 'md',
    overflow: 'hidden',
    border: '1px solid #E4E0EF',
    flexShrink: '0',
  },
})

const ImageThumb = styled('img', {
  base: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
})

const ThumbRemoveButton = styled('button', {
  base: {
    position: 'absolute',
    top: '2px',
    right: '2px',
    width: '20px',
    height: '20px',
    borderRadius: 'pill',
    border: 'none',
    backgroundColor: 'rgba(26, 19, 48, 0.62)',
    color: '#FFFFFF',
    fontSize: '12px',
    lineHeight: '1',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
})

const UploadingTile = styled('div', {
  base: {
    width: '72px',
    height: '72px',
    borderRadius: 'md',
    border: '1px solid #E4E0EF',
    backgroundColor: '#F7F6FB',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'body',
    fontSize: '11px',
    color: 'quickpass.inkSoft',
    flexShrink: '0',
  },
})

const AddImageTile = styled('label', {
  base: {
    width: '72px',
    height: '72px',
    borderRadius: 'md',
    border: '1.5px dashed #C9C2E0',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '2px',
    fontFamily: 'body',
    fontSize: '11px',
    fontWeight: '600',
    color: 'quickpass.inkSoft',
    cursor: 'pointer',
    flexShrink: '0',
    transition: 'all {durations.fast} {easings.appleStandard}',
    _hover: { borderColor: 'quickpass.primary', color: 'quickpass.primary' },
  },
})

const HiddenFileInput = styled('input', {
  base: {
    display: 'none',
  },
})

const PostImageGrid = styled('div', {
  base: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginTop: '10px',
  },
})

const PostImageButton = styled('button', {
  base: {
    width: '96px',
    height: '96px',
    borderRadius: 'md',
    overflow: 'hidden',
    border: '1px solid #E4E0EF',
    padding: '0',
    backgroundColor: '#F7F6FB',
    cursor: 'zoom-in',
    flexShrink: '0',
    transition: 'opacity {durations.fast} {easings.appleStandard}',
    _hover: { opacity: '0.9' },
  },
})

const PostImage = styled('img', {
  base: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
})

const LightboxBackdrop = styled('div', {
  base: {
    position: 'fixed',
    inset: '0',
    zIndex: '100',
    backgroundColor: 'rgba(26, 19, 48, 0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
    cursor: 'zoom-out',
  },
})

const LightboxImage = styled('img', {
  base: {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
    borderRadius: 'md',
    boxShadow: '0 24px 60px -20px rgba(0, 0, 0, 0.6)',
  },
})

export {
  PageStack,
  PageHeader,
  PageTitle,
  PageSubtitle,
  QuietText,
  TabRow,
  TabButton,
  HeaderRow,
  SubHeading,
  PrimaryButton,
  GhostButton,
  Composer,
  FieldLabel,
  TextInput,
  TextArea,
  SelectInput,
  ComposerActions,
  PostList,
  PostTitleRow,
  PostTitle,
  ChipRow,
  CategoryChip,
  ResolvedBadge,
  AdminBadge,
  PostMeta,
  PostBody,
  ActionRow,
  ActionButton,
  Divider,
  ReplyList,
  ReplyItem,
  ReplyHandleRow,
  ReplyHandle,
  ReplyBody,
  ReplyForm,
  ReportPanel,
  InlineNote,
  ErrorBanner,
  NoticeBanner,
  VoteControl,
  VoteButton,
  VoteScore,
  ImageRow,
  ImageThumbWrapper,
  ImageThumb,
  ThumbRemoveButton,
  UploadingTile,
  AddImageTile,
  HiddenFileInput,
  PostImageGrid,
  PostImageButton,
  PostImage,
  LightboxBackdrop,
  LightboxImage,
}
